import type { PosterAssetDto } from "../../api/posterService";

/** lightbox 一帧:原始 asset + 版本标注 + 是否为最新版。 */
export interface LightboxSlide {
  asset: PosterAssetDto;
  /** "原图" | "改版 2" | "改版 3" … 按可预览资产的 seq 顺位算。 */
  versionLabel: string;
  /** 该任务存活的最大 seq(= 最新那张)。 */
  isLatest: boolean;
}

/**
 * 把一个海报任务的产物列表整成 lightbox 幻灯片:
 * - 只保留 SUCCESS 且有可用 URL(baseUrl/thumbUrl)的资产;
 * - **长图的分段资产不算版本**(见下);
 * - 按 seq 升序排(原图 → 最新),不信后端返回顺序;
 * - 标注版本:最早一张「原图」,其后「改版 N」;最大 seq 标 isLatest。
 *
 * 用户改版 / 图改图 / 品牌变体在同一任务里追加更大 seq 的新行(见 backend
 * PosterAssetEditService maxSeq+1),故「最新那张 = 最大 seq」。
 *
 * ═══ ★长图分段必须排除(2026-07-29 走查实测)═══════════════════════════════════
 * 长图任务的产物是 **seq=1 整图 + seq≥2 每块一张分段**(assetMeta.segment=true)。
 * 这里原来一视同仁按 seq 排,于是 7 个区块被标成「改版 2…改版 8」、末位还标了「最新」
 * 并成为默认选中项 —— 用户在作品库点开一条长图,看到的是**最后一个区块**(实测 750×294),
 * 想看长图得往左翻 7 次。而「改版 7」这个标签本身就是假的:那不是改版,是区块。
 *
 * 分段有它自己的入口(详情页「分段」面板,可单块重出),不该混进版本链。
 * ★兜底:万一整图缺失导致过滤后为空,退回不过滤 —— 宁可显示分段也不要一个空 lightbox。
 */
export function buildLightboxSlides(assets: PosterAssetDto[]): LightboxSlide[] {
  const previewable = assets.filter((a) => a.status === "SUCCESS" && (a.baseUrl || a.thumbUrl));
  const withoutSegments = previewable.filter((a) => a.segment !== true);
  const valid = (withoutSegments.length > 0 ? withoutSegments : previewable).sort(
    (a, b) => a.seq - b.seq
  );
  const lastIdx = valid.length - 1;
  return valid.map((asset, i) => ({
    asset,
    versionLabel: i === 0 ? "原图" : `改版 ${i + 1}`,
    isLatest: i === lastIdx,
  }));
}
